import { useEffect, useMemo, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import { fromDateTimeLocalValue } from '../lib/dateTime';
import { createFamilyClient } from '../lib/familyClient';

const initialCreateFamilyForm = { name: '' };
const initialCreateInviteForm = { email: '', role: 'member' };

function extractInviteToken(invite) {
  return (
    invite?.token ||
    invite?.inviteToken ||
    invite?.shareToken ||
    invite?.data?.token ||
    invite?.data?.inviteToken ||
    ''
  );
}

function resolveFamilyWorkspace(payload) {
  return {
    family: payload?.family || payload?.data?.family || null,
    membership: payload?.membership || payload?.data?.membership || null,
    requestId: payload?.requestId || payload?.data?.requestId || '',
  };
}

function resolveArray(payload, keys) {
  if (Array.isArray(payload)) {
    return payload;
  }

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }

    if (Array.isArray(payload?.data?.[key])) {
      return payload.data[key];
    }
  }

  return [];
}

function resolveTask(payload) {
  return payload?.task || payload?.data?.task || payload || null;
}

function buildFamilyTaskFilters(filter, currentUserId) {
  if (filter === 'mine') {
    return {
      assignedTo: currentUserId || '',
    };
  }

  if (filter === 'all') {
    return {};
  }

  return {
    status: filter,
  };
}

function normalizeOptionalString(value) {
  if (typeof value !== 'string') {
    return value ?? null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

function buildFamilyTaskPayload(taskData) {
  return {
    title: typeof taskData?.title === 'string' ? taskData.title.trim() : '',
    description: normalizeOptionalString(taskData?.description),
    assignedTo: normalizeOptionalString(taskData?.assignedTo),
    priority: taskData?.priority || 'medium',
    dueDate: normalizeOptionalString(fromDateTimeLocalValue(taskData?.dueDate)),
    status: taskData?.status || 'open',
    recurrence: taskData?.recurrence ?? null,
  };
}

function buildFamilyTaskUpdatePayload(updates) {
  const payload = { ...updates };

  if (Object.prototype.hasOwnProperty.call(payload, 'description')) {
    payload.description = normalizeOptionalString(payload.description);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'assignedTo')) {
    payload.assignedTo = normalizeOptionalString(payload.assignedTo);
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'dueDate')) {
    payload.dueDate = normalizeOptionalString(fromDateTimeLocalValue(payload.dueDate));
  }

  return payload;
}

function getFamilyTaskSuccessMessage(action, payload) {
  if (action === 'create') {
    return payload?.assignedTo ? 'Task created and assigned successfully.' : 'Task created successfully.';
  }

  if (action === 'complete') {
    return 'Task completed successfully.';
  }

  switch (payload?.status) {
    case 'in_progress':
      return 'Task marked in progress.';
    case 'done':
      return 'Task marked done.';
    case 'cancelled':
      return 'Task cancelled.';
    default:
      if (Object.prototype.hasOwnProperty.call(payload || {}, 'assignedTo')) {
        return payload?.assignedTo
          ? 'Task assignment updated successfully.'
          : 'Task unassigned successfully.';
      }

      return 'Task updated successfully.';
  }
}

function useFamily(currentUser) {
  const client = useMemo(() => createFamilyClient(getBackendUrl()), []);
  const currentUserId = currentUser?.userId || '';
  const [family, setFamily] = useState(null);
  const [membership, setMembership] = useState(null);
  const [familyRequestId, setFamilyRequestId] = useState('');
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyInvites, setFamilyInvites] = useState([]);
  const [incomingFamilyInvites, setIncomingFamilyInvites] = useState([]);
  const [familyTasks, setFamilyTasks] = useState([]);
  const [familySharedLists, setFamilySharedLists] = useState([]);
  
  const [familyStatus, setFamilyStatus] = useState({
    type: 'idle',
    message: 'No family workspace exists yet.',
  });
  const [createFamilyStatus, setCreateFamilyStatus] = useState({
    type: 'idle',
    message: '',
  });
  const [inviteStatus, setInviteStatus] = useState({
    type: 'idle',
    message: '',
  });
  const [familyTasksStatus, setFamilyTasksStatus] = useState({
    type: 'idle',
    message: '',
  });

  const [createFamilyForm, setCreateFamilyForm] = useState(initialCreateFamilyForm);
  const [createInviteForm, setCreateInviteForm] = useState(initialCreateInviteForm);
  const [familyTaskFilter, setFamilyTaskFilter] = useState('open');
  const [acceptingIncomingInviteId, setAcceptingIncomingInviteId] = useState('');
  const [revokingInviteId, setRevokingInviteId] = useState('');
  const [lastCreatedInviteToken, setLastCreatedInviteToken] = useState('');

  // Load family data
  useEffect(() => {
    if (!currentUserId) return;

    async function loadFamilyData() {
      try {
        setFamilyStatus({ type: 'loading', message: 'Loading family...' });
        
        const familyResponse = await client.getFamily(currentUserId);
        const resolvedWorkspace = resolveFamilyWorkspace(familyResponse);
        setFamily(resolvedWorkspace.family);
        setMembership(resolvedWorkspace.membership);
        setFamilyRequestId(resolvedWorkspace.requestId);

        const members = await client.getFamilyMembers(currentUserId);
        setFamilyMembers(resolveArray(members, ['members']));

        const invites = await client.getFamilyInvites(currentUserId);
        setFamilyInvites(resolveArray(invites, ['invites']));

        const incomingInvites = await client.getIncomingFamilyInvites(currentUserId);
        setIncomingFamilyInvites(resolveArray(incomingInvites, ['incomingInvites', 'invites']));

        const tasks = await client.getFamilyTasks(
          currentUserId,
          buildFamilyTaskFilters(familyTaskFilter, currentUserId)
        );
        setFamilyTasks(resolveArray(tasks, ['tasks']));

        const lists = await client.getFamilyLists(currentUserId);
        setFamilySharedLists(resolveArray(lists, ['lists']));

        setFamilyStatus({ type: 'success', message: 'Family loaded.' });
      } catch (error) {
        setFamilyStatus({
          type: 'error',
          message: error.error || error.message || 'Failed to load family.',
        });
      }
    }

    loadFamilyData();
  }, [client, currentUserId, familyTaskFilter]);

  async function handleCreateFamily(event, familyName) {
    event?.preventDefault?.();

    if (!currentUser?.userId) {
      setCreateFamilyStatus({ type: 'error', message: 'Please sign in first.' });
      return;
    }

    if (!familyName?.trim()) {
      setCreateFamilyStatus({ type: 'error', message: 'Family name is required.' });
      return;
    }

    setCreateFamilyStatus({ type: 'loading', message: 'Creating family...' });

    try {
      const newFamily = await client.createFamily(currentUser.userId, { name: familyName });
      const resolvedWorkspace = resolveFamilyWorkspace(newFamily);
      setFamily(resolvedWorkspace.family);
      setMembership(resolvedWorkspace.membership);
      setFamilyRequestId(resolvedWorkspace.requestId);
      setCreateFamilyForm(initialCreateFamilyForm);
      setCreateFamilyStatus({ type: 'success', message: 'Family created!' });
    } catch (error) {
      setCreateFamilyStatus({
        type: 'error',
        message: error.error || error.message || 'Failed to create family.',
      });
    }
  }

  async function handleCreateInvite(event) {
    event?.preventDefault?.();

    if (!currentUser?.userId) {
      setInviteStatus({ type: 'error', message: 'Please sign in first.' });
      return;
    }

    if (!createInviteForm.email?.trim()) {
      setInviteStatus({ type: 'error', message: 'Email is required.' });
      return;
    }

    setInviteStatus({ type: 'loading', message: 'Creating invite...' });

    try {
      const newInvite = await client.createFamilyInvite(currentUser.userId, {
        email: createInviteForm.email,
        role: createInviteForm.role || 'member',
      });
      setFamilyInvites((current) => [...current, newInvite]);
      setCreateInviteForm(initialCreateInviteForm);
      setLastCreatedInviteToken(extractInviteToken(newInvite));
      setInviteStatus({ type: 'success', message: 'Invite sent!' });
    } catch (error) {
      setInviteStatus({
        type: 'error',
        message: error.error || error.message || 'Failed to create invite.',
      });
    }
  }

  async function handleAcceptInvite(token) {
    if (!currentUser?.userId) {
      setInviteStatus({ type: 'error', message: 'Please sign in first.' });
      return;
    }

    setInviteStatus({ type: 'loading', message: 'Accepting invite...' });

    try {
      await client.acceptFamilyInvite(currentUser.userId, { token });
      setInviteStatus({ type: 'success', message: 'Invite accepted!' });
      // Reload family data
      const familyResponse = await client.getFamily(currentUser.userId);
      const resolvedWorkspace = resolveFamilyWorkspace(familyResponse);
      setFamily(resolvedWorkspace.family);
      setMembership(resolvedWorkspace.membership);
      setFamilyRequestId(resolvedWorkspace.requestId);
      const members = await client.getFamilyMembers(currentUser.userId);
      setFamilyMembers(resolveArray(members, ['members']));
    } catch (error) {
      setInviteStatus({
        type: 'error',
        message: error.error || error.message || 'Failed to accept invite.',
      });
    }
  }

  async function handleAcceptIncomingInvite(inviteId) {
    if (!currentUser?.userId) {
      setInviteStatus({ type: 'error', message: 'Please sign in first.' });
      return;
    }

    setInviteStatus({ type: 'loading', message: 'Accepting invite...' });
    setAcceptingIncomingInviteId(inviteId);

    try {
      await client.acceptIncomingFamilyInvite(currentUser.userId, inviteId);
      setIncomingFamilyInvites((current) =>
        current.filter((inv) => (inv?._id || inv?.id) !== inviteId)
      );
      setInviteStatus({ type: 'success', message: 'Invite accepted!' });
      // Reload family data
      const familyResponse = await client.getFamily(currentUser.userId);
      const resolvedWorkspace = resolveFamilyWorkspace(familyResponse);
      setFamily(resolvedWorkspace.family);
      setMembership(resolvedWorkspace.membership);
      setFamilyRequestId(resolvedWorkspace.requestId);
    } catch (error) {
      setInviteStatus({
        type: 'error',
        message: error.error || error.message || 'Failed to accept invite.',
      });
    } finally {
      setAcceptingIncomingInviteId('');
    }
  }

  async function handleRevokeInvite(inviteId) {
    if (!currentUser?.userId) {
      setInviteStatus({ type: 'error', message: 'Please sign in first.' });
      return;
    }

    setRevokingInviteId(inviteId);

    try {
      await client.revokeFamilyInvite(currentUser.userId, inviteId);
      setFamilyInvites((current) =>
        current.filter((inv) => (inv?._id || inv?.id) !== inviteId)
      );
    } catch (error) {
      setInviteStatus({
        type: 'error',
        message: error.error || error.message || 'Failed to revoke invite.',
      });
    } finally {
      setRevokingInviteId('');
    }
  }

  async function handleCreateFamilyTask(event, taskData) {
    event?.preventDefault?.();

    if (!currentUser?.userId) {
      setFamilyTasksStatus({ type: 'error', message: 'Please sign in first.' });
      return false;
    }

    const payload = buildFamilyTaskPayload(taskData);

    if (!payload.title) {
      setFamilyTasksStatus({ type: 'error', message: 'Task title is required.' });
      return false;
    }

    setFamilyTasksStatus({ type: 'loading', message: 'Creating task...' });

    try {
      await client.createFamilyTasks(currentUser.userId, { tasks: [payload] });
      const tasks = await client.getFamilyTasks(
        currentUser.userId,
        buildFamilyTaskFilters(familyTaskFilter, currentUser.userId)
      );
      setFamilyTasks(resolveArray(tasks, ['tasks']));
      setFamilyTasksStatus({
        type: 'success',
        message: getFamilyTaskSuccessMessage('create', payload),
      });
      return true;
    } catch (error) {
      setFamilyTasksStatus({
        type: 'error',
        message: error.error || error.message || 'Failed to create task.',
      });
      return false;
    }
  }

  async function handleUpdateFamilyTask(taskId, updates) {
    if (!currentUser?.userId) {
      setFamilyTasksStatus({ type: 'error', message: 'Please sign in first.' });
      return false;
    }

    const payload = buildFamilyTaskUpdatePayload(updates);
    setFamilyTasksStatus({ type: 'loading', message: 'Updating task...' });

    try {
      const updated = resolveTask(await client.updateFamilyTask(currentUser.userId, taskId, payload));
      setFamilyTasks((current) =>
        current.map((task) =>
          (task?._id || task?.id) === taskId ? { ...task, ...(updated || payload) } : task
        )
      );
      setFamilyTasksStatus({
        type: 'success',
        message: getFamilyTaskSuccessMessage('update', payload),
      });
      return true;
    } catch (error) {
      setFamilyTasksStatus({
        type: 'error',
        message: error.error || error.message || 'Failed to update task.',
      });
      return false;
    }
  }

  async function handleCompleteFamilyTask(taskId) {
    if (!currentUser?.userId) {
      setFamilyTasksStatus({ type: 'error', message: 'Please sign in first.' });
      return false;
    }

    setFamilyTasksStatus({ type: 'loading', message: 'Completing task...' });

    try {
      const completedTask = resolveTask(await client.completeFamilyTask(currentUser.userId, taskId));
      setFamilyTasks((current) =>
        current.map((task) =>
          (task?._id || task?.id) === taskId
            ? { ...task, ...(completedTask || {}), status: 'done' }
            : task
        )
      );
      setFamilyTasksStatus({
        type: 'success',
        message: getFamilyTaskSuccessMessage('complete'),
      });
      return true;
    } catch (error) {
      setFamilyTasksStatus({
        type: 'error',
        message: error.error || error.message || 'Failed to complete task.',
      });
      return false;
    }
  }

  return {
    family,
    membership,
    familyRequestId,
    familyMembers,
    familyInvites,
    incomingFamilyInvites,
    familyTasks,
    familySharedLists,
    familyStatus,
    createFamilyStatus,
    inviteStatus,
    familyTasksStatus,
    createFamilyForm,
    createInviteForm,
    familyTaskFilter,
    acceptingIncomingInviteId,
    revokingInviteId,
    lastCreatedInviteToken,
    setCreateFamilyForm,
    setCreateInviteForm,
    setFamilyTaskFilter,
    handleCreateFamily,
    handleCreateInvite,
    handleAcceptInvite,
    handleAcceptIncomingInvite,
    handleRevokeInvite,
    handleCreateFamilyTask,
    handleUpdateFamilyTask,
    handleCompleteFamilyTask,
  };
}

export default useFamily;
